/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import moment from 'moment';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage
} from '@ion/core/src/capabilities/storage';
import {PreferencesStore} from '@ion/core/src/preferences/types';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  safeJSONParse,
  safeJSONStringify
} from '@ion/core/src/utils/JSONUtils';

/**
 * Provides the results of the `resolvePreferences` function, which contains the
 * preferences, and an `isStale` property which indicates how the preferences were
 * resolved.
 *
 * When `isStale` is `true`, the preferences were restored from localStorage, and
 * is used to indicate that the User Preferences should be immediately saved. If
 * `false`, indicates that the preferences from the Platform are the most current
 * and need not be saved.
 */
export type ResolverResults = {
  preferences: PreferencesStore,
  isStale: boolean
}

/**
 * Provides an internal API for generating the temporary preferences key based on
 * the given User Id.
 *
 * @param   userId The id of the `User` for which the temporary preferences are to
 *          be persisted to localStorage.
 * @returns The localStorage key used to persist the temporary user preferences.
 */
const getKey = (userId: number): string => (
  `user-${userId}-temporary-preferences`
);

/**
 * @internal
 *
 * Given a `User` Id, `lastModifiedOn` date, and `lastSavedOn` date, determines
 * if there are temporarily persisted user preferences, and if they have been saved
 * locally after the User's `extendedAttributes` have last been saved, in which case
 * this indicates that the latest user preferences have not been saved, and the
 * locally persisted preferences are to be used (and immediately save to CTC),
 * otherwise, the current User Preferences as obtained from the extendedAttributes
 * will be returned.
 *
 * **Important Note: This is an internal function that is proxied exclusively via the
 * `resolvePreferences` function of the UserPreferencesAutoSave API**
 *
 * @param   userId The id of the `User` for which the temporary preferences are to
 *          be retrieved from localStorage.
 * @param   lastModifiedOn `lastModifiedOn` date of the User's `extendedAttributes`,
 *          this value is used to determine if the CTC persisted preferences have
 *          been saved after the locally persisted user preferences, in which case
 *          the Platform preferences are to be used, otherwise, if the preferences
 *          persisted locally are more recent than those persisted to the Platform,
 *          the locally persisted preferences are to be used, and subsequently saved
 *          to the Platform.
 * @returns The results of the preferences resolution, which contain the preferences,
 *          and an `isStale` property which indicates how the preferences were resolved.
 *          When `isStale` is `true`, the preferences were restored from localStorage,
 *          and is used to indicate that the User Preferences should be immediately
 *          saved. If `false`, indicates that the preferences from the Platform are
 *          the most current and need not be saved.
 */
export const resolvePreferences = (
  userId: number, lastModifiedOn: string, preferences: PreferencesStore): ResolverResults => {
  const key = getKey(userId);
  const persisted = getLocalStorage(key);
  let results: ResolverResults;
  if (persisted?.lastModifiedOn) {
    if (moment(persisted.lastModifiedOn).isAfter(moment(lastModifiedOn))) {
      results = {
        preferences : safeJSONParse(persisted.preferences),
        isStale     : true
      };
    }
  }
  deleteLocalStorage(key);
  return results || {preferences, isStale: false};
};

/**
 * @internal
 *
 * To be invoked when the User's Session has expired, at which point, if there are
 * unsaved User Preferences, the current preferences will be persisted locally and
 * subsequently restored upon the next login if the local preferences are newer than
 * the current external preferences from the user's extended attributes.
 *
 * **Important Note: This is an internal function that is proxied exclusively via the
 * `setSessionExpired` function of the UserPreferencesAutoSave API**
 *
 * ```typescript
 *  // packages/<ion-app>/store/actions/login.ts
 *  ...
 *
 *  export const sessionTimeout = () => {
 *    // if the session has expired and there are unsaved preferences, persist them
 *    // locally for subsequent restoring ...
 *    if (UserPreferences.hasChanges(true)) {
 *      setSessionExpired();
 *    }
 *    return () => {
 *      deleteLocalStorage(ApplicationKeys.CTCAdminSession);
 *      location.assign(getRoute(Route.LOGIN));
 *    };
 *  };
 * ```
 *
 * @param userId The id of the `User` for which the temporary preferences are to
 *        be persisted to localStorage when the user's session has expired.
 * @param the user preferences which are to be persisted locally for subsequent
 *        restoration if they are more recent than  the preferences retrieved
 *        from the user's `extendedAttributes`.
 * @param offset When `true`, indicates that the `lastModifiedOn` date associated
 *        with the locally persisted user preferences is to have an offset of one
 *        minute. This optional offset allows for persisting a fallback of the user
 *        preferences which can be used for specific use-cases, such as when the
 *        user's session has expired and the browser is closed, at which point
 *        the user's preferences will not be able to be saved to the platform.
 *        Upon the next login, if they preferences were not saved to the platform,
 *        the fallback preferences will be restored and subsequently saved to CTC.
 */
export const setTemporaryPreferences = (userId: number, preferences: GenericObject, offset = false) => {
  const lastModifiedOn = (offset ? moment().subtract(2, 'minutes') : moment()).toISOString();
  setLocalStorage(getKey(userId), {
    preferences : safeJSONStringify(preferences),
    lastModifiedOn
  });
};
